import { cn } from '@/lib/utils.ts'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button.tsx'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.tsx'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx'

import { MarkdownViewer } from '@/components/MarkdownViewer.tsx'
import { MentionInputComponent, getMentionSuggestionsStyle } from '@/components/Mention.tsx'
import { UserAvatar } from '@/components/UserAvatar.tsx'
import { Attachment } from '@/components/form/Attachment.tsx'
import { DropdownItem } from '@/components/form/DropdownItem.tsx'
import { FileUploadButton } from '@/components/form/FileUploadButton.tsx'
import { useAttachment } from '@/contexts/AttachmentContext.tsx'
import { useUserContext } from '@/contexts/UserContext.tsx'
import { useCommentMutation } from '@/requests/mutations/useMutateEntity.ts'
import { toReadableDateTime } from '@/utils/date.ts'
import { getTemporaryId, isEntityUpdate, isTemporaryId } from '@/utils/key.ts'
import { metaEnterSubmission } from '@/utils/keyboard.ts'

type Props = {
  modes: AvailableMode[]
  comment: StoryComment
  placeholder: string
}

export const Comment = ({ modes, comment, placeholder }: Props) => {
  // biome-ignore lint/style/noNonNullAssertion: This is guaranteed to be non-null
  const user = useUserContext().user!
  const [content, setContent] = useState(comment.content)
  const [isEditMode, setIsEditMode] = useState(modes[0] === 'edit')
  const { create, update, remove } = useCommentMutation(comment.id)
  const {
    addAttachment,
    initializeAttachments,
    getCommentAttachments,
    removeAttachment,
    uploadProgress,
    downloadAttachment,
  } = useAttachment()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const displayDate = useMemo(() => {
    const time = (!isTemporaryId(comment.id) || comment.__typename) && comment.updatedAt
    if (!time) return
    const isEdited = comment.createdAt !== comment.updatedAt

    return (
      <div className="flex gap-1 items-center font-EN-KR-caption-regular text-tb-navy-100">
        <time aria-label={toReadableDateTime(time)}>{toReadableDateTime(time)}</time>
        {isEdited && (
          <>
            <img src="/images/dot-icon.svg" alt="·" />
            <p>Edited</p>
          </>
        )}
      </div>
    )
  }, [comment])

  const attachments = getCommentAttachments(comment.id)

  const onSubmit = () => {
    if (isEntityUpdate(comment)) {
      if (attachments.length === 0 && comment.attachments.length !== 0 && !content) {
        remove(comment.id)
      } else {
        update({
          content,
          attachments,
        })
      }
      setIsEditMode(false)
    } else {
      const newCommentId = getTemporaryId()
      create({
        ...comment,
        id: newCommentId,
        content,
        attachments: attachments.filter((att) => att.uploadId).map((att) => ({ ...att, commentId: newCommentId })),
      })
      setContent('')
    }
    attachments.map((attachment) => removeAttachment(attachment.id))
  }

  const newAttachments = useMemo(() => {
    return attachments.some((attachment) => attachment.uploadId)
  }, [attachments])

  const hasAttachmentsChanged = useMemo(() => {
    const initial = comment.attachments || []
    if (attachments.length !== initial.length) return true
    const initialIds = new Set(initial.map((a) => a.id))
    return attachments.some((a) => !initialIds.has(a.id))
  }, [comment.attachments, attachments])

  const hasChanges = content !== '' || hasAttachmentsChanged || newAttachments

  const handleManualFileUpload = async (files: File[]) => {
    await addAttachment(files, comment.storyId, comment.id)
  }

  return (
    <div className="py-2">
      {isEditMode ? (
        <div className="flex flex-col bg-surface-backgroundElevated border rounded-xs border-border-default-neutral focus-within:border-border-default-focused">
          <div className="flex gap-2 px-3 py-2">
            <UserAvatar member={comment.user} size="xs" />
            <div className="flex-grow">
              <MentionInputComponent
                value={content}
                onChange={(e: { target: { value: string } }) => setContent(e.target.value)}
                style={getMentionSuggestionsStyle({ fontSize: '14px', lineHeight: '16px' })}
                placeholder={placeholder}
                allowSuggestionsAboveCursor
                classNames={{
                  'mentions__highlighter': '!font-EN-KR-body2-regular',
                  'mentions__control': cn(
                    'w-full min-h-[100px] p-1 overflow-scroll !font-EN-KR-body2-regular text-contents-primary bg-surface-backgroundElevated',
                    '!ring-0 !ring-offset- focus-visible:!outline-0 focus-visible:!ring-0 focus-visible:!border-0',
                  ),
                  'mentions__input': cn(
                    '!font-EN-KR-body2-regular w-full p-2 overflow-scroll placeholder:!text-contents-placeholder p-0',
                    '!ring-0 !ring-offset-0 focus-visible:!outline-0 focus-visible:!ring-0 focus-visible:!border-0',
                  ),
                }}
                onPaste={(e) => {
                  if (!e.clipboardData) {
                    e.stopPropagation()
                    return
                  }
                  const files = e.clipboardData.files
                  if (!files?.length) {
                    return
                  }

                  e.stopPropagation()

                  const file = files[0]
                  if (!file?.type.includes('image')) {
                    return
                  }

                  e.preventDefault()
                  void addAttachment([file], comment.storyId, comment.id)
                }}
                markup="**@__display__**"
                onKeyDown={(e) => metaEnterSubmission(e, onSubmit)}
              />
              {attachments.map((attachment) => {
                const uploadId = attachment.uploadId
                const progress = uploadId ? uploadProgress[uploadId]?.progress : undefined
                return (
                  <Attachment
                    key={attachment.id}
                    attachment={attachment}
                    onRemove={() => removeAttachment(attachment.id)}
                    onDownload={undefined}
                    isEditMode={isEditMode}
                    progress={progress}
                  />
                )
              })}
            </div>
          </div>

          <div className="flex justify-between items-center px-2 py-0 h-12">
            <FileUploadButton onUpload={handleManualFileUpload} />
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                className="rounded-xs w-[50px] h-8 px-[3px] py-1 font-EN-KR-body2-regular text-contents-primary"
                onClick={() => {
                  if (modes.includes('view')) {
                    setContent(comment.content)
                    initializeAttachments([comment])
                    setIsEditMode(false)
                  } else {
                    setContent('')
                    for (const attachment of attachments) {
                      removeAttachment(attachment.id)
                    }
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={!isEntityUpdate(comment) && !hasChanges}
                onClick={onSubmit}
                className={cn(
                  'py-2 px-1.5 h-8 rounded-xs font-EN-KR-body2-bold',
                  isEntityUpdate(comment) ? 'w-[62px]' : 'w-[46px]',
                )}
              >
                {isEntityUpdate(comment) ? 'Update' : 'Post'}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <UserAvatar member={comment.user} size="xs" />
          <div className="flex flex-col gap-1 flex-grow overflow-hidden">
            <div className="flex justify-between items-center">
              <span className="font-EN-KR-body2-bold text-contents-primary">{comment.user.name}</span>
              <Popover>
                <PopoverTrigger asChild disabled={user.id.toString() !== comment.user.id.toString()}>
                  <Button type="button" variant="ghost" className="w-[17px] h-[17px] p-0 rounded-xs">
                    <img src="/images/comment-options-icon.svg" alt="more" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  alignOffset={-8}
                  className="flex flex-col items-start w-[88px] px-0 py-1 rounded-xs bg-surface-componentBackground"
                >
                  {user.id.toString() === comment.user.id.toString() && (
                    <>
                      <DropdownItem displayText="Edit" onClick={() => modes.includes('edit') && setIsEditMode(true)} />
                      <DropdownItem displayText="Delete" onClick={() => setIsDeleteDialogOpen(true)} />
                    </>
                  )}
                </PopoverContent>
              </Popover>
            </div>
            <MarkdownViewer value={comment.content} variant="comment" />
            {attachments.map((attachment) => {
              const uploadId = attachment.uploadId
              const progress = uploadId ? uploadProgress[uploadId]?.progress : undefined
              return (
                <Attachment
                  key={attachment.id}
                  attachment={attachment}
                  onRemove={() => removeAttachment(attachment.id)}
                  onDownload={() => downloadAttachment(attachment.id)}
                  isEditMode={isEditMode}
                  progress={progress}
                />
              )
            })}
            {displayDate}
          </div>
          <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
            <DialogContent className="p-4 gap-6 [&>button]:hidden">
              <DialogHeader>
                <DialogTitle className="font-EN-KR-sub-heading-bold text-contents-primary">Delete Comment?</DialogTitle>
                <DialogDescription className="font-EN-KR-body1-regular text-contents-primary">
                  Are you sure you want to delete this comment?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="secondary" onClick={() => setIsDeleteDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    remove(comment.id)
                    setIsDeleteDialogOpen(false)
                  }}
                >
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  )
}
